import type { CompareProduct, ProductVariant } from '../types';

const EMPTY_CELL = `
  <div class="w-[144px] m:w-[200px] flex-1 shrink-0 p-2x">—</div>
`;

export function renderVariantRow(
  label: string,
  optionIndex: number,
  products: CompareProduct[],
  maxCompare = 3,
): string {
  const cellData = products.map((p) => {
    const variants = p.variants || [];

    if (variants.length === 0) {
      return { html: EMPTY_CELL, hasData: false };
    }

    const getVariantValue = (variant: ProductVariant): string => {
      if (!variant.options || !p.optionNames) {
        return '—';
      }

      const values = Array.isArray(variant.options) ? variant.options : (variant.options || '').split(' / ');
      const optionNames = (p.optionNames || '').split('||');
      const index = optionNames.findIndex((name) => name === label);

      if (index === -1) {
        return '—';
      }

      const rawValue = values[index]?.trim();

      if (!rawValue || rawValue === 'Default Title' || rawValue === 'Title') {
        return '—';
      }

      return rawValue;
    };

    const activeId = p.currentVariantId || variants[0]?.id.toString();
    const currentVariant = variants.find((v) => v.id.toString() === activeId.toString());

    const rawValues = Array.from(new Set(variants.filter((v) => v.available).map(getVariantValue)));
    const filteredValues = rawValues.filter((value) => value && value !== '-' && value !== '—');

    if (filteredValues.length === 0) {
      return { html: EMPTY_CELL, hasData: false };
    }

    const html = `
        <div
          class="w-[144px] m:w-[200px] flex-1 shrink-0 p-2x"
          data-option-index="${optionIndex}"
        >
          <div class="body-m m:body-l mb-2x current-val-display">
            ${
              currentVariant && getVariantValue(currentVariant) !== '-' && getVariantValue(currentVariant) !== '—'
                ? getVariantValue(currentVariant)
                : ''
            }
          </div>

          <div class="flex flex-wrap gap-1x">
            ${filteredValues
              .map((value) => {
                const variant = variants.find((v) => getVariantValue(v) === value);
                const isSelected = currentVariant && getVariantValue(currentVariant) === value;

                return `
                  <button
                    type="button"
                    class="
                      variant-swap-btn
                      border-xs
                      px-2x
                      h-[24px]
                      label-s
                      rounded-xs
                      transition-all
                      ${
                        isSelected
                          ? 'bg-mono text-content-primary-inverse border-primary'
                          : 'bg-primary text-content-primary border-tertiary'
                      }
                    "
                    data-product-id="${p.id}"
                    data-variant-id="${variant?.id}"
                    data-price="${variant?.price || p.price}"
                    data-compare-price="${variant?.compare_at_price || ''}"
                    data-has-discount="${!!(
                      variant?.compare_at_price && variant.compare_at_price !== (variant?.price || p.price)
                    )}"
                  >
                    ${value}
                  </button>
                `;
              })
              .join('')}
          </div>
        </div>
      `;

    return { html, hasData: true };
  });

  if (!cellData.some((c) => c.hasData)) {
    return '';
  }

  return `
    <div class="compare-row mb-1x flex flex-col px-2x py-3x rounded-m border-secondary hover:bg-secondary">
      <div class="label-m tracking-wider mb-1x px-2x">
        ${label}
      </div>

      <div class="flex gap-2x m:gap-6x body-l items-start">
        ${cellData.map((c) => c.html).join('')}

        ${Array(maxCompare - products.length)
          .fill(EMPTY_CELL)
          .join('')}
      </div>
    </div>
  `;
}
